import React from 'react'
import { Controller, FieldError, FieldValues, Path, useFormContext } from 'react-hook-form'

import { InputTag } from '@/app/component/atom/form'

type Props<T extends FieldValues> = {
  name: Path<T>
  label?: string
  helperText?: React.ReactNode
  autoFocus?: boolean
  disabled?: boolean
}

export function RHFInputTag<T extends FieldValues>({ name, helperText, ...props }: Props<T>) {
  const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement>()

  const [inputValue, setInputValue] = React.useState('')
  const { control } = useFormContext<T>()

  React.useEffect(() => {
    if (!props.autoFocus) {
      return undefined
    }

    const timeout = setTimeout(() => {
      inputRef.current?.focus({
        preventScroll: true,
      })
    }, 0) // to push it to the end of the event loop

    return () => {
      clearTimeout(timeout)
    }
  }, [props.autoFocus])

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onBlur, onChange }, fieldState: { error } }) => {
        const safeValue = Array.isArray(value) ? value : []
        return (
          <InputTag
            error={!!error}
            helperText={getErrorMessage(error) || helperText}
            inputRef={inputRef}
            tags={safeValue}
            value={inputValue}
            onBlur={onBlur}
            onChange={(val: string) => setInputValue(val)}
            onDelete={(index: number) => {
              const newTags = [...safeValue]
              newTags.splice(index, 1)
              onChange(newTags)
            }}
            onKeyPress={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault()
                if (inputValue.trim()) {
                  const cleansedInput = inputValue.replace(/(?:\r\n|\r|\n)/g, ',')
                  const newTags = cleansedInput
                    .split(',')
                    .map((v) => v.trim())
                    .filter((v) => !!v)

                  if (newTags.length > 0) {
                    onChange([...safeValue, ...newTags])
                    setInputValue('')
                  }
                }
              }
            }}
            {...props}
          />
        )
      }}
    ></Controller>
  )
}

export const getErrorMessage = (error?: FieldError | FieldError[]): string => {
  if (!error) {
    return ''
  }

  if (Array.isArray(error)) {
    // Get the first error message if it's an array
    // Why find? because there's can be an empty error object
    // in the array for non-error items: [empty, {message: 'bla'}]
    return error.find((err) => err?.message)?.message || ''
  }

  return error.message || ''
}
